import React, { useState } from 'react'
import { EuiCheckbox, EuiFieldText, EuiFlexGroup, EuiFlexItem, EuiIcon, EuiButton, EuiFormRow, EuiPanel, EuiSelect, EuiSpacer } from '@equipmentshare/ds2'
import { BuildingLineIcon, AddCircleLineIcon, FileTextLineIcon } from '@equipmentshare/ds2-icons'
import { InventoryLocation } from 'inventory-web'
import InventoryLocationPicker from '../../../components/InventoryLocationPicker'
import PhoneInput from '../../../components/PhoneInput'
import { AlternateDeliveryAddress, FormValidation, isValidPostalCode } from '../hooks/useDemandRequestForm'
import { UseDistributionCenterOptionsReturn } from '../hooks/useDistributionCenterOptions'
import NotesModal from './NotesModal'
import EmailInput from '../../../components/EmailInput'
import { US_STATE_OPTIONS } from '../constants/usStates'

export type HeaderFormProps = {
	// Form data
	requestingInventoryId: string,
	contactPhone: string,
	notes: string,
  ccEmail: string,
	deliverToAlternateAddress: boolean,
	alternateAddress: AlternateDeliveryAddress,

	// Form setters
	onRequestingInventoryChange: ( id: number, inventory?: InventoryLocation ) => void,
	onFulfillingInventoryChange: ( id: string ) => void,
	onContactPhoneChange: ( phone: string ) => void,
	onContactPhoneBlur: ( phone: string ) => void,
	onNotesChange: ( notes: string ) => void,
  onCCEmailChange: ( email: string ) => void,
  onCCEmailBlur: ( email: string ) => void,
	onDeliverToAlternateAddressChange: ( value: boolean ) => void,
	onAlternateAddressFieldChange: ( field: keyof AlternateDeliveryAddress, value: string ) => void,

	// Validation
	validation: FormValidation,
	// Alternate-address errors are only surfaced once the user has attempted a
	// submit. The validation itself is live (so the submit button stays disabled),
	// but showing "Required" on all four fields the instant the checkbox is
	// ticked — before anything has been typed — reads as broken. Mirrors
	// LineItemsList.
	showValidationErrors?: boolean,

	// Selected inventory objects (for display)
	selectedRequestingInventory?: InventoryLocation,

	// The DC the request goes to ('' = Automatic) and the DCs it may go to
	fulfillingInventoryId: string,
	distributionCenters: UseDistributionCenterOptionsReturn,
}

// Automatic leaves the DC to inventory-api's routing
const AUTOMATIC_OPTION_VALUE = ''

export default function HeaderForm( {
	requestingInventoryId,
	contactPhone,
	ccEmail,
	notes,
	deliverToAlternateAddress,
	alternateAddress,
	onRequestingInventoryChange,
	onFulfillingInventoryChange,
	onContactPhoneChange,
	onContactPhoneBlur,
	onNotesChange,
	onCCEmailChange,
	onCCEmailBlur,
	onDeliverToAlternateAddressChange,
	onAlternateAddressFieldChange,
	validation,
	showValidationErrors = false,
	selectedRequestingInventory,
	fulfillingInventoryId,
	distributionCenters,
}: HeaderFormProps ) {
	const [ isNotesModalOpen, setIsNotesModalOpen ] = useState( false )

	// A required alternate-address field is only flagged once the address as a
	// whole is invalid AND the user has tried to submit.
	const isAlternateAddressFieldMissing = ( field: keyof AlternateDeliveryAddress ) =>
		showValidationErrors && validation.alternateAddress.isInvalid && !alternateAddress[ field ].trim()

	// A filled-in ZIP can still be wrong (e.g. 4 digits), so the ZIP row has a
	// format error on top of the shared "Required".
	const getPostalCodeError = () => {
		if ( isAlternateAddressFieldMissing( 'postalCode' ) ) {
			return 'Required'
		}
		if ( showValidationErrors && alternateAddress.postalCode.trim() && !isValidPostalCode( alternateAddress.postalCode ) ) {
			return 'Enter a 5-digit ZIP or ZIP+4'
		}
		return undefined
	}
	const postalCodeError = getPostalCodeError()

	const handleOpenNotesModal = () => {
		setIsNotesModalOpen( true )
	}

	const handleCloseNotesModal = () => {
		setIsNotesModalOpen( false )
	}

	const handleSaveNotes = ( newNotes: string ) => {
		onNotesChange( newNotes )
	}

	const hasNotes = notes && notes.trim().length > 0

	// Served by a mini-DC: Automatic, the serving mini-DCs, and the main DC. Not
	// served: only the main DC, and the picker is locked to it.
	const { servedByMiniDc } = distributionCenters
	const distributionCenterOptions = [
		...( servedByMiniDc ? [ { value: AUTOMATIC_OPTION_VALUE, text: 'Automatic' } ] : [] ),
		...distributionCenters.options.map( ( dc ) => ( { value: dc.id, text: dc.name } ) ),
	]

	return (
		<EuiFlexGroup direction="column" gutterSize="m">
			{/* Store Section Header with Icon */}
			<EuiFlexItem>
				<EuiFlexGroup gutterSize="s" alignItems="center">
					<EuiFlexItem grow={false}>
						<EuiIcon type={BuildingLineIcon} size="m" />
					</EuiFlexItem>
					<EuiFlexItem grow={false}>
						<strong>Store</strong>
					</EuiFlexItem>
				</EuiFlexGroup>
			</EuiFlexItem>

			{/* Request For, Distribution Center, Phone, and Add Note button all on same line */}
			<EuiFlexItem>
				<EuiFlexGroup gutterSize="m" alignItems="flexStart" wrap={false}>
					{/* Request For */}
					<EuiFlexItem grow={false} style={{ minWidth: 300 }}>
						<InventoryLocationPicker
							id="requesting-inventory"
							label={
								<>
									Request For{' '}
									{!requestingInventoryId && <span className="required">*</span>}
								</>
							}
							selectedInventoryLocation={selectedRequestingInventory}
							onChange={onRequestingInventoryChange}
							formRowProps={{
								isInvalid: validation.requestingInventoryId.isInvalid,
								error: validation.requestingInventoryId.error,
							}}
							placeholder="Select requesting store"
							data-testid="requesting-inventory-picker"
						/>
					</EuiFlexItem>

					{/* Distribution Center — editable only where a mini-DC serves the requesting location */}
					<EuiFlexItem grow={false} style={{ minWidth: 300 }}>
						<EuiFormRow
							label="Distribution Center"
							helpText={servedByMiniDc ? 'Automatic routes the request to a serving DC' : undefined}
						>
							<EuiSelect
								id="fulfilling-inventory"
								options={distributionCenterOptions}
								value={fulfillingInventoryId}
								onChange={( e ) => onFulfillingInventoryChange( e.target.value )}
								disabled={!servedByMiniDc}
								isLoading={distributionCenters.loading}
								data-testid="fulfilling-inventory-picker"
							/>
						</EuiFormRow>
					</EuiFlexItem>

					{/* Contact Phone */}
					<EuiFlexItem grow={false} style={{ minWidth: 200 }}>
						<PhoneInput
							label={<>Contact Phone <span className="required">*</span></>}
							value={contactPhone}
							onChange={onContactPhoneChange}
							onBlur={onContactPhoneBlur}
							isInvalid={validation.contactPhone.isInvalid}
							error={validation.contactPhone.error}
						/>
					</EuiFlexItem>

					{/* CC Email */}
					<EuiFlexItem grow={false} style={{ minWidth: 300 }}>
						<EmailInput
							label={'Additional Email (optional)'}
							value={ccEmail}
							onChange={onCCEmailChange}
							onBlur={onCCEmailBlur}
							isInvalid={validation.ccEmail.isInvalid}
							error={validation.ccEmail.error}
							placeholder="email@equipmentshare.com"
						/>
					</EuiFlexItem>

					{/* Add Note button — empty label space keeps it aligned with the inputs */}
					<EuiFlexItem grow={false}>
						<EuiFormRow hasEmptyLabelSpace>
							<EuiButton
								iconType={hasNotes ? FileTextLineIcon : AddCircleLineIcon}
								onClick={handleOpenNotesModal}
								color={hasNotes ? 'success' : 'primary'}
								fill={false}
								data-testid="header-add-note-button"
							>
								{hasNotes ? 'Edit Note' : 'Add Note'}
							</EuiButton>
						</EuiFormRow>
					</EuiFlexItem>
				</EuiFlexGroup>
			</EuiFlexItem>

			{/* Deliver to alternate address */}
			<EuiFlexItem>
				<EuiCheckbox
					id="deliver-to-alternate-address"
					checked={deliverToAlternateAddress}
					onChange={( e ) => onDeliverToAlternateAddressChange( e.target.checked )}
					label="Deliver to alternate location"
					data-testid="deliver-to-alternate-address-checkbox"
				/>
			</EuiFlexItem>

			{deliverToAlternateAddress && (
				<EuiFlexItem>
					<EuiPanel paddingSize="m" hasBorder hasShadow={false} color="subdued">
						{/* Street lines and city/state/ZIP are split across two rows,
						    matching the "Ship to" block in CreateFulfillmentOrderModal. */}
						<EuiFlexGroup gutterSize="m" wrap responsive={false}>
							<EuiFlexItem style={{ minWidth: 280 }}>
								<EuiFormRow
									label={<>Street Address <span className="required">*</span></>}
									fullWidth
									isInvalid={isAlternateAddressFieldMissing( 'line1' )}
									error={isAlternateAddressFieldMissing( 'line1' ) ? 'Required' : undefined}
								>
									<EuiFieldText
										fullWidth
										placeholder="123 Main St"
										isInvalid={isAlternateAddressFieldMissing( 'line1' )}
										value={alternateAddress.line1}
										onChange={( e ) => onAlternateAddressFieldChange( 'line1', e.target.value )}
										data-testid="alternate-address-line1"
									/>
								</EuiFormRow>
							</EuiFlexItem>
							<EuiFlexItem style={{ minWidth: 280 }}>
								<EuiFormRow label="Address Line 2" fullWidth>
									<EuiFieldText
										fullWidth
										placeholder="Suite, unit, building (optional)"
										value={alternateAddress.line2}
										onChange={( e ) => onAlternateAddressFieldChange( 'line2', e.target.value )}
										data-testid="alternate-address-line2"
									/>
								</EuiFormRow>
							</EuiFlexItem>
						</EuiFlexGroup>
						<EuiSpacer size="m" />
						<EuiFlexGroup gutterSize="m" wrap responsive={false}>
							<EuiFlexItem style={{ minWidth: 200 }}>
								<EuiFormRow
									label={<>City <span className="required">*</span></>}
									fullWidth
									isInvalid={isAlternateAddressFieldMissing( 'city' )}
									error={isAlternateAddressFieldMissing( 'city' ) ? 'Required' : undefined}
								>
									<EuiFieldText
										fullWidth
										placeholder="Austin"
										isInvalid={isAlternateAddressFieldMissing( 'city' )}
										value={alternateAddress.city}
										onChange={( e ) => onAlternateAddressFieldChange( 'city', e.target.value )}
										data-testid="alternate-address-city"
									/>
								</EuiFormRow>
							</EuiFlexItem>
							<EuiFlexItem style={{ minWidth: 140 }}>
								<EuiFormRow
									label={<>State <span className="required">*</span></>}
									fullWidth
									isInvalid={isAlternateAddressFieldMissing( 'state' )}
									error={isAlternateAddressFieldMissing( 'state' ) ? 'Required' : undefined}
								>
									<EuiSelect
										fullWidth
										hasNoInitialSelection
										options={US_STATE_OPTIONS}
										isInvalid={isAlternateAddressFieldMissing( 'state' )}
										value={alternateAddress.state}
										onChange={( e ) => onAlternateAddressFieldChange( 'state', e.target.value )}
										data-testid="alternate-address-state"
									/>
								</EuiFormRow>
							</EuiFlexItem>
							<EuiFlexItem style={{ minWidth: 200 }}>
								<EuiFormRow
									label={<>ZIP Code <span className="required">*</span></>}
									fullWidth
									isInvalid={!!postalCodeError}
									error={postalCodeError}
								>
									<EuiFieldText
										fullWidth
										placeholder="78701"
										isInvalid={!!postalCodeError}
										value={alternateAddress.postalCode}
										onChange={( e ) => onAlternateAddressFieldChange( 'postalCode', e.target.value )}
										data-testid="alternate-address-postal-code"
									/>
								</EuiFormRow>
							</EuiFlexItem>
						</EuiFlexGroup>
					</EuiPanel>
				</EuiFlexItem>
			)}

			{/* Notes Modal */}
			<NotesModal
				isOpen={isNotesModalOpen}
				onClose={handleCloseNotesModal}
				notes={notes}
				onSave={handleSaveNotes}
				title={hasNotes ? 'Edit Note' : 'Add Note'}
				placeholder="Add any additional notes..."
			/>
		</EuiFlexGroup>
	)
}
